import { ConfirmModal } from "@cmp-src/overlay/modals/ConfirmModal";
import { Button } from "@cmp-src/primitives/button";
import { Division } from "@gettactic/api";
import { ExpandIcon } from "@gettactic/components/src/icons/TacticIcons/ExpandIcon";
import { useDisclosure } from "@gettactic/helpers/src/hooks";
import { useAuthenticated } from "@gettactic/hooks";
import { CogIcon, XIcon } from "@heroicons/react/outline";
import React from "react";
import { DivisionMapEditor } from "@/features/divisions/components/DivisionMapEditor";
import { MapBuilderFloorSwitcher } from "@/features/map/components/MapBuilderFloorSwitcher";
import type { UseMapViewSvgEditReturn } from "@/features/map/components/MapView/MapViewSvg/useMapViewSvgEdit";
import { useCanEditManagedZones } from "@/features/map/hooks/useCanEditManagedZones";
import { useMap } from "@/features/map/MapProvider";
import { useIsMapBuilderEditorAllowed } from "@/lib/useIsMapBuilderEditorAllowed";

export interface MapActionsProps {
  editor?: UseMapViewSvgEditReturn;
}

export const MapActions: React.FC<MapActionsProps> = ({ editor }) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const {
    floor,
    isEmbedded,
    isMapBuilderMap,
    setForceMapBuilder,
    editingMapDisclosure,
    fullscreenMapDisclosure,
    viewingDivision,
    setViewingDivision
  } = useMap();
  const editConfirmDisclosure = useDisclosure();
  const canEditMapBuilder = useIsMapBuilderEditorAllowed();
  const allowEditMode = authenticatedUser?.permissions.settings;
  const canEditManagedZones = useCanEditManagedZones(floor);
  // Campus (division) currently open in the editor — starts as the viewed
  // campus, and the header switcher can move it to another campus.
  const [editedDivision, setEditedDivision] = React.useState<Division | null>(
    null
  );

  // Campus map view: the floor editor doesn't apply — the edit button
  // opens the division map builder instead.
  if (viewingDivision) {
    return (
      <div className="mr-8 hidden items-center md:flex">
        {allowEditMode && !isEmbedded ? (
          <Button
            size={"sm"}
            onClick={() => setEditedDivision(viewingDivision)}
            type="button"
            iconRight={<CogIcon className="h-4 w-4" />}
            className={"items-center"}
          >
            Open Map Builder
          </Button>
        ) : null}
        {editedDivision ? (
          <DivisionMapEditor
            key={editedDivision.id}
            division={editedDivision}
            onClose={() => setEditedDivision(null)}
            headerSlot={
              <MapBuilderFloorSwitcher
                divisionId={editedDivision.id}
                onSwitchDivision={setEditedDivision}
              />
            }
            onSaved={(mapData) => {
              // Refresh the campus view in place — the map canvas reloads
              // when its value changes. Only when the edited campus is the
              // one being viewed.
              setViewingDivision((prev) =>
                prev && prev.id === editedDivision.id
                  ? { ...prev, map_data: mapData }
                  : prev
              );
            }}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="mr-8 hidden items-center md:flex">
      {editor?.isResourcePanelEditorOpen || editor?.isMapBuilderEditorOpen ? (
        <>
          <ConfirmModal
            size={"xl"}
            title={"Unsaved changes"}
            isOpen={editConfirmDisclosure.isOpen}
            onOpenChange={editConfirmDisclosure.onOpenChange}
            onConfirm={async () => {
              await editor?.mapBuilderRef?.save({ mode: "activate-now" });
              window.location.reload();
            }}
            onExtra={() => {
              editingMapDisclosure.close();
              editor?.closeMapBuilderEditor();
              editConfirmDisclosure.close();
            }}
            cancelLabel={"Stay Here"}
            extraLabel={"Exit without save"}
            confirmLabel={"Save and close"}
          >
            Are you sure you want to exit the map edit mode? All your unsaved
            changes will be lost.
          </ConfirmModal>
          <Button
            size={"sm"}
            onClick={() => {
              if (isMapBuilderMap) {
                editConfirmDisclosure.open();
                return;
              }
              editor?.endEdit();
            }}
            type="button"
            iconRight={<XIcon className="h-4 w-4" />}
            className={"items-center"}
          >
            {isMapBuilderMap ? "Exit Map Builder" : "Exit Map Edit Mode"}
          </Button>
        </>
      ) : (
        <div className={"flex gap-2"}>
          {allowEditMode && !isEmbedded && !fullscreenMapDisclosure.isOpen ? (
            <Button
              size={"sm"}
              onClick={async () => {
                if (isMapBuilderMap) {
                  editor?.openMapBuilderEditor();
                  return;
                }
                editor?.openResourceEditPanel();
              }}
              type="button"
              iconRight={<CogIcon className="h-4 w-4" />}
              className={"items-center"}
            >
              {isMapBuilderMap ? "Open Map Builder" : "Map Edit Mode"}
            </Button>
          ) : null}
          {canEditManagedZones &&
          isMapBuilderMap &&
          !isEmbedded &&
          !fullscreenMapDisclosure.isOpen ? (
            <Button
              size={"sm"}
              onClick={() => editor?.openMapBuilderEditor()}
              type="button"
              iconRight={<CogIcon className="h-4 w-4" />}
              className={"items-center"}
            >
              Edit desks in my zones
            </Button>
          ) : null}
          {!isMapBuilderMap &&
          canEditMapBuilder &&
          allowEditMode &&
          !isEmbedded &&
          !fullscreenMapDisclosure.isOpen ? (
            <Button
              size={"sm"}
              onClick={() => setForceMapBuilder(true)}
              type="button"
              iconRight={<CogIcon className="h-4 w-4" />}
              className={"items-center"}
            >
              Force Map Builder
            </Button>
          ) : undefined}
          {!isEmbedded && (
            <Button
              size={"sm"}
              onClick={() => fullscreenMapDisclosure.toggle()}
              type="button"
              className={"items-center"}
            >
              <ExpandIcon />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
